import { useState, useEffect, useCallback } from "react";
import {
  getDomainConfig,
  getDefaultDomain,
  getAllDomains,
} from "../constants/domains";

export const useDomainConfig = () => {
  const [selectedDomain, setSelectedDomain] = useState(getDefaultDomain());
  const [domainConfig, setDomainConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force updates

  // ✅ FIXED: Load initial domain config immediately
  useEffect(() => {
    const savedDomain = localStorage.getItem("selectedDomain");
    const initialDomain = savedDomain || getDefaultDomain();

    console.log(
      `🔄 useDomainConfig: Initial setup with domain: ${initialDomain}`
    );
    console.log(`📦 localStorage selectedDomain: "${savedDomain}"`);
    console.log(`🎯 Default domain: "${getDefaultDomain()}"`);
    console.log(`🗂️ All localStorage keys:`, Object.keys(localStorage));

    // ✅ FIXED: Always set the correct initial domain
    setSelectedDomain(initialDomain);

    // ✅ Load initial config immediately
    const initialConfig = getDomainConfig(initialDomain);
    if (initialConfig) {
      setDomainConfig(initialConfig);
      console.log(`🚀 Initial domain config loaded:`, {
        domain: initialDomain,
        domainName: initialConfig.name,
        defaultProjectType: initialConfig.defaults?.projectType,
        defaultContentType: initialConfig.defaults?.contentType,
        projectTypesCount: Object.keys(initialConfig.projectTypes || {}).length,
        contentTypesCount: Object.keys(initialConfig.contentTypes || {}).length,
      });
    } else {
      console.error(`❌ Failed to load initial config for: ${initialDomain}`);
    }

    setIsLoading(false);
  }, []); // ✅ Remove selectedDomain dependency to avoid loop

  // Update domain config when selectedDomain changes or refreshKey changes
  useEffect(() => {
    console.log(
      `useDomainConfig: Updating config for domain: ${selectedDomain}, refreshKey: ${refreshKey}`
    );
    const config = getDomainConfig(selectedDomain);

    if (config) {
      setDomainConfig(config);
      console.log(`✅ Domain config updated successfully:`, {
        domain: selectedDomain,
        domainName: config.name,
        defaultProjectType: config.defaults?.projectType,
        defaultContentType: config.defaults?.contentType,
        projectTypesCount: Object.keys(config.projectTypes || {}).length,
        contentTypesCount: Object.keys(config.contentTypes || {}).length,
        labels: config.labels,
      });
    } else {
      console.error(`❌ Failed to get domain config for: ${selectedDomain}`);
    }
  }, [selectedDomain, refreshKey]);

  const switchDomain = useCallback(
    (domainId) => {
      console.log(
        `🔄 useDomainConfig: Switching domain from ${selectedDomain} to ${domainId}`
      );

      if (domainId === selectedDomain) {
        console.log(`⚠️ Same domain selected, skipping switch`);
        return;
      }

      setSelectedDomain(domainId);
      localStorage.setItem("selectedDomain", domainId);
      // Force refresh to ensure all components get the new config
      setRefreshKey((prev) => prev + 1);

      console.log(`✅ Domain switch initiated: ${domainId}`);
      console.log(`📦 localStorage updated: "${domainId}"`);
    },
    [selectedDomain]
  );

  // Force refresh domain config
  const refreshDomainConfig = useCallback(() => {
    console.log(`🔄 Forcing domain config refresh for: ${selectedDomain}`);
    setRefreshKey((prev) => prev + 1);
  }, [selectedDomain]);

  return {
    selectedDomain,
    domainConfig,
    switchDomain,
    refreshDomainConfig,
    allDomains: getAllDomains(),
    isLoading,
    refreshKey, // Expose refresh key for debugging
  };
};
